import { ReactElement, useEffect } from 'react';
import {
    UIEmptyState,
    UIErrorState,
    UILoadingState,
    UISection,
    UISectionContent,
    useUINav
} from '@/ui';
import { useCompanyContext, useMember } from '@/companies/providers';
import { usePagesTranslation } from '../../hooks';
import { useFetchUserAppDevices } from '@/companies/queries';
import { useViewAppDevicesAction } from '@/companies/actions';
import { MemberAppDevicesSection } from '@/companies/components';

export function MemberIdAppDevicesSettingsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { company } = useCompanyContext();
    const { companyUser } = useMember();

    const { isAllowed: canViewAppDevices } = useViewAppDevicesAction({
        user: companyUser
    });

    const { appDevices, status, error, canRetry, refetch } =
        useFetchUserAppDevices(companyUser.id);

    const name = companyUser.joined
        ? `${companyUser.firstName} ${companyUser.lastName}`
        : companyUser.email;

    const { setParts } = useUINav();
    useEffect(() => {
        setParts([
            { text: t('shared.labels.settings') },
            {
                text: t('shared.labels.members'),
                link: `/company/${company.id}/settings/members`
            },
            { text: name },
            { text: t('shared.labels.app_devices') }
        ]);
    }, [setParts, company.id, name, t]);

    // Seeing the member is not enough to see their devices, so say who can
    // rather than leaving the tab looking broken. The server's own reason for
    // the denial is too generic to help here.
    if (!canViewAppDevices) {
        return (
            <UIEmptyState
                emoji="🔒"
                title={t('pages.member_id_app_devices_settings.hidden_title')}
                description={t(
                    'pages.member_id_app_devices_settings.hidden_description'
                )}
            />
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {status === 'pending' && (
                <UISection>
                    <UISectionContent>
                        <UILoadingState
                            text={t(
                                'pages.member_id_app_devices_settings.loading'
                            )}
                        />
                    </UISectionContent>
                </UISection>
            )}

            {status === 'error' && (
                <UISection>
                    <UISectionContent>
                        <UIErrorState
                            title={error}
                            onRetry={canRetry ? refetch : undefined}
                        />
                    </UISectionContent>
                </UISection>
            )}

            {appDevices !== undefined && (
                <MemberAppDevicesSection appDevices={appDevices} />
            )}
        </div>
    );
}
